import * as api from '../../../api';
import { TmRisk } from '../models/tmRisk';
import { TmActor } from '../models/tmActor';
import { TmMitigation } from '../models/tmMitigation';
import { TmAssessment } from '../models/tmAssessment';
import { setTmRisks, addTmRisk, updateTmRisk, removeTmRisk } from '../store/tmRisk/tmRiskSlice';
import { setTmActors, addTmActor, updateTmActor, removeTmActor } from '../store/tmActor/tmActorSlice';
import { setTmMitigations, addTmMitigation, updateTmMitigation, removeTmMitigation } from '../store/tmMitigation/tmMitigationSlice';
import { setTmAssessments, addTmAssessment, updateTmAssessment, removeTmAssessment } from '../store/tmAssessment/tmAssessmentSlice';
import { ChatToolRegistry } from '../../../utils/chatTools';

/**
 * Register all Threat Modeling tools with the chat tool registry
 */
export function registerTmTools(registry: ChatToolRegistry): void {
  // === TmRisk Tools ===

  registry.register(
    'list_tm_risks',
    {
      type: 'function',
      function: {
        name: 'list_tm_risks',
        description: 'List all threat modeling risks for the current assessment. Use this to see what risks exist.',
        parameters: {
          type: 'object',
          properties: {
            assessment_id: {
              type: 'integer',
              description: 'The assessment ID to filter risks by (optional, uses current assessment if not specified)',
            },
          },
        },
      },
    },
    async (args, context) => {
      const assessmentId = args.assessment_id || context.assessmentId;
      const risks = await api.listTmRisks(assessmentId);

      // Update Redux state if dispatch is available
      if (context.dispatch) {
        context.dispatch(setTmRisks(risks));
      }

      return {
        success: true,
        data: risks,
        message: `Found ${risks.length} risk(s). IMPORTANT: You now have all the risks data. Analyze this data and respond to the user with a text message. DO NOT call list_tm_risks or any other list tools again - you already have the information you need.`,
      };
    }
  );

  registry.register(
    'create_tm_risk',
    {
      type: 'function',
      function: {
        name: 'create_tm_risk',
        description: 'Create a new threat modeling risk. Use this when the user wants to add a new risk.',
        parameters: {
          type: 'object',
          properties: {
            description: {
              type: 'string',
              description: 'A clear description of the risk',
            },
            explanation: {
              type: 'string',
              description: 'Detailed explanation of the risk (optional)',
            },
            chance: {
              type: 'integer',
              description: 'Likelihood score from 0-5',
              minimum: 0,
              maximum: 5,
            },
            impact: {
              type: 'integer',
              description: 'Impact score from 0-5',
              minimum: 0,
              maximum: 5,
            },
            chance_after_mitigation: {
              type: 'integer',
              description: 'Residual likelihood score after mitigation (0-5)',
              minimum: 0,
              maximum: 5,
            },
            impact_after_mitigation: {
              type: 'integer',
              description: 'Residual impact score after mitigation (0-5)',
              minimum: 0,
              maximum: 5,
            },
            status: {
              type: 'string',
              description: 'Risk status (e.g., "open", "closed", "mitigated")',
              enum: ['open', 'closed', 'mitigated', 'accepted'],
            },
            actor_id: {
              type: 'integer',
              description: 'ID of the threat actor (optional)',
            },
          },
          required: ['description', 'chance', 'impact'],
        },
      },
    },
    async (args, context) => {
      if (!context.assessmentId) {
        throw new Error('No assessment selected');
      }

      const riskData: Omit<TmRisk, 'id'> = {
        description: args.description,
        explanation: args.explanation || '',
        chance: args.chance,
        impact: args.impact,
        chance_after_mitigation: args.chance_after_mitigation || 0,
        impact_after_mitigation: args.impact_after_mitigation || 0,
        status: args.status || 'open',
        assessment_id: context.assessmentId,
        actor_id: args.actor_id || null,
        mitigation_ids: [],
      };

      const createdRisk = await api.createTmRisk(riskData);

      // Update Redux state if dispatch is available
      if (context.dispatch) {
        context.dispatch(addTmRisk(createdRisk));
      }

      return {
        success: true,
        data: createdRisk,
        message: `Created risk: ${createdRisk.description}`,
      };
    }
  );

  registry.register(
    'update_tm_risk',
    {
      type: 'function',
      function: {
        name: 'update_tm_risk',
        description: 'Update an existing threat modeling risk. Can update by ID or by matching description. Use this to modify risk properties like chance, impact, or status.',
        parameters: {
          type: 'object',
          properties: {
            risk_id: {
              type: 'integer',
              description: 'The ID of the risk to update (required if not using description_match)',
            },
            description_match: {
              type: 'string',
              description: 'Match risk by description (fuzzy match, use if ID is unknown)',
            },
            description: {
              type: 'string',
              description: 'New description for the risk',
            },
            explanation: {
              type: 'string',
              description: 'New explanation',
            },
            chance: {
              type: 'integer',
              description: 'New likelihood score (0-5)',
              minimum: 0,
              maximum: 5,
            },
            impact: {
              type: 'integer',
              description: 'New impact score (0-5)',
              minimum: 0,
              maximum: 5,
            },
            chance_after_mitigation: {
              type: 'integer',
              description: 'New residual likelihood score (0-5)',
              minimum: 0,
              maximum: 5,
            },
            impact_after_mitigation: {
              type: 'integer',
              description: 'New residual impact score (0-5)',
              minimum: 0,
              maximum: 5,
            },
            status: {
              type: 'string',
              description: 'New status',
              enum: ['open', 'closed', 'mitigated', 'accepted'],
            },
            actor_id: {
              type: 'integer',
              description: 'New actor ID',
            },
          },
        },
      },
    },
    async (args, context) => {
      let riskId = args.risk_id;

      // If description_match is provided, find the risk by description
      if (!riskId && args.description_match) {
        const risks = await api.listTmRisks(context.assessmentId);
        const matchedRisk = risks.find((r) =>
          r.description.toLowerCase().includes(args.description_match.toLowerCase())
        );
        if (!matchedRisk || !matchedRisk.id) {
          throw new Error(`No risk found matching: ${args.description_match}`);
        }
        riskId = matchedRisk.id;
      }

      if (!riskId) {
        throw new Error('Either risk_id or description_match is required');
      }

      const updateData: any = {};
      if (args.description !== undefined) updateData.description = args.description;
      if (args.explanation !== undefined) updateData.explanation = args.explanation;
      if (args.chance !== undefined) updateData.chance = args.chance;
      if (args.impact !== undefined) updateData.impact = args.impact;
      if (args.chance_after_mitigation !== undefined)
        updateData.chance_after_mitigation = args.chance_after_mitigation;
      if (args.impact_after_mitigation !== undefined)
        updateData.impact_after_mitigation = args.impact_after_mitigation;
      if (args.status !== undefined) updateData.status = args.status;
      if (args.actor_id !== undefined) updateData.actor_id = args.actor_id;

      const updatedRisk = await api.updateTmRiskById(riskId, updateData);

      // Update Redux state if dispatch is available
      if (context.dispatch) {
        context.dispatch(updateTmRisk(updatedRisk));
      }

      return {
        success: true,
        data: updatedRisk,
        message: `Updated risk: ${updatedRisk.description}`,
      };
    }
  );

  registry.register(
    'bulk_update_tm_risks',
    {
      type: 'function',
      function: {
        name: 'bulk_update_tm_risks',
        description: 'Update multiple risks at once. Use this for operations like "close all risks" or "set all risks to status X".',
        parameters: {
          type: 'object',
          properties: {
            filter: {
              type: 'object',
              description: 'Filter criteria to select which risks to update',
              properties: {
                status: {
                  type: 'string',
                  description: 'Filter by current status',
                },
                min_chance: {
                  type: 'integer',
                  description: 'Minimum chance score',
                },
                max_chance: {
                  type: 'integer',
                  description: 'Maximum chance score',
                },
                all: {
                  type: 'boolean',
                  description: 'Update all risks (no filter)',
                },
              },
            },
            updates: {
              type: 'object',
              description: 'Fields to update on matched risks',
              properties: {
                status: {
                  type: 'string',
                  description: 'New status for all matched risks',
                },
                chance: {
                  type: 'integer',
                  minimum: 0,
                  maximum: 5,
                },
                impact: {
                  type: 'integer',
                  minimum: 0,
                  maximum: 5,
                },
              },
            },
          },
          required: ['updates'],
        },
      },
    },
    async (args, context) => {
      const risks = await api.listTmRisks(context.assessmentId);

      // Apply filters
      let filteredRisks = risks;
      if (args.filter) {
        filteredRisks = risks.filter((risk) => {
          if (args.filter.all) return true;
          if (args.filter.status && risk.status !== args.filter.status) return false;
          if (args.filter.min_chance !== undefined && risk.chance < args.filter.min_chance) return false;
          if (args.filter.max_chance !== undefined && risk.chance > args.filter.max_chance) return false;
          return true;
        });
      }

      // Update each risk
      const updatePromises = filteredRisks.map((risk) =>
        risk.id ? api.updateTmRiskById(risk.id, args.updates) : Promise.resolve(null)
      );

      const updatedRisks = await Promise.all(updatePromises);
      const successCount = updatedRisks.filter((r) => r !== null).length;

      // Update Redux state for each updated risk
      if (context.dispatch) {
        updatedRisks.filter((r) => r !== null).forEach(risk => {
          context.dispatch(updateTmRisk(risk!));
        });
      }

      return {
        success: true,
        data: updatedRisks.filter((r) => r !== null),
        message: `Updated ${successCount} risk(s)`,
      };
    }
  );

  registry.register(
    'delete_tm_risk',
    {
      type: 'function',
      function: {
        name: 'delete_tm_risk',
        description: 'Delete a threat modeling risk by ID. Use this when the user wants to remove a risk.',
        parameters: {
          type: 'object',
          properties: {
            risk_id: {
              type: 'integer',
              description: 'The ID of the risk to delete',
            },
          },
          required: ['risk_id'],
        },
      },
    },
    async (args, context) => {
      await api.deleteTmRiskById(args.risk_id);

      // Update Redux state if dispatch is available
      if (context.dispatch) {
        context.dispatch(removeTmRisk(args.risk_id));
      }

      return {
        success: true,
        message: `Deleted risk with ID ${args.risk_id}`,
      };
    }
  );

  // === TmActor Tools ===

  registry.register(
    'list_tm_actors',
    {
      type: 'function',
      function: {
        name: 'list_tm_actors',
        description: 'List all threat actors in the current assessment. Use this to see available actors.',
        parameters: {
          type: 'object',
          properties: {},
        },
      },
    },
    async (args, context) => {
      const actors = await api.listTmActors(context.assessmentId);

      // Update Redux state if dispatch is available
      if (context.dispatch) {
        context.dispatch(setTmActors(actors));
      }

      return {
        success: true,
        data: actors,
        message: `Found ${actors.length} actor(s). IMPORTANT: You now have all the actors data. Analyze this data and respond to the user with a text message. DO NOT call list_tm_actors or any other list tools again - you already have the information you need.`,
      };
    }
  );

  registry.register(
    'create_tm_actor',
    {
      type: 'function',
      function: {
        name: 'create_tm_actor',
        description: 'Create a new threat actor. Use this when the user wants to add a new actor.',
        parameters: {
          type: 'object',
          properties: {
            name: {
              type: 'string',
              description: 'The name of the threat actor',
            },
          },
          required: ['name'],
        },
      },
    },
    async (args, context) => {
      if (!context.assessmentId) {
        throw new Error('No assessment selected');
      }

      const actorData: Omit<TmActor, 'id'> = {
        name: args.name,
        assessment_id: context.assessmentId,
      };

      const createdActor = await api.createTmActor(actorData);

      // Update Redux state if dispatch is available
      if (context.dispatch) {
        context.dispatch(addTmActor(createdActor));
      }

      return {
        success: true,
        data: createdActor,
        message: `Created actor: ${createdActor.name}`,
      };
    }
  );

  registry.register(
    'update_tm_actor',
    {
      type: 'function',
      function: {
        name: 'update_tm_actor',
        description: 'Update an existing threat actor. Use this to modify actor properties.',
        parameters: {
          type: 'object',
          properties: {
            actor_id: {
              type: 'integer',
              description: 'The ID of the actor to update',
            },
            name: {
              type: 'string',
              description: 'New name for the actor',
            },
          },
          required: ['actor_id'],
        },
      },
    },
    async (args, context) => {
      const updateData: any = {};
      if (args.name !== undefined) updateData.name = args.name;

      const updatedActor = await api.updateTmActorById(args.actor_id, updateData);

      // Update Redux state if dispatch is available
      if (context.dispatch) {
        context.dispatch(updateTmActor(updatedActor));
      }

      return {
        success: true,
        data: updatedActor,
        message: `Updated actor: ${updatedActor.name}`,
      };
    }
  );

  registry.register(
    'delete_tm_actor',
    {
      type: 'function',
      function: {
        name: 'delete_tm_actor',
        description: 'Delete a threat actor by ID. Use this when the user wants to remove an actor.',
        parameters: {
          type: 'object',
          properties: {
            actor_id: {
              type: 'integer',
              description: 'The ID of the actor to delete',
            },
          },
          required: ['actor_id'],
        },
      },
    },
    async (args, context) => {
      await api.deleteTmActorById(args.actor_id);

      // Update Redux state if dispatch is available
      if (context.dispatch) {
        context.dispatch(removeTmActor(args.actor_id));
      }

      return {
        success: true,
        message: `Deleted actor with ID ${args.actor_id}`,
      };
    }
  );

  // === TmMitigation Tools ===

  registry.register(
    'list_tm_mitigations',
    {
      type: 'function',
      function: {
        name: 'list_tm_mitigations',
        description: 'List all mitigations in the current assessment. Use this to see available mitigations.',
        parameters: {
          type: 'object',
          properties: {},
        },
      },
    },
    async (args, context) => {
      const mitigations = await api.listTmMitigations(context.assessmentId);

      // Update Redux state if dispatch is available
      if (context.dispatch) {
        context.dispatch(setTmMitigations(mitigations));
      }

      return {
        success: true,
        data: mitigations,
        message: `Found ${mitigations.length} mitigation(s). IMPORTANT: You now have all the mitigations data. Analyze this data and respond to the user with a text message. DO NOT call list_tm_mitigations or any other list tools again - you already have the information you need.`,
      };
    }
  );

  registry.register(
    'create_tm_mitigation',
    {
      type: 'function',
      function: {
        name: 'create_tm_mitigation',
        description: 'Create a new mitigation and optionally link it to one or more risks. Use this when the user wants to add a new mitigation.',
        parameters: {
          type: 'object',
          properties: {
            description: {
              type: 'string',
              description: 'A clear description of the mitigation',
            },
            status: {
              type: 'string',
              description: 'Status of the mitigation (e.g., "planned", "in_progress", "completed")',
            },
            priority: {
              type: 'integer',
              description: 'Priority level (0-5)',
              minimum: 0,
              maximum: 5,
            },
            effort: {
              type: 'integer',
              description: 'Effort required (0-5)',
              minimum: 0,
              maximum: 5,
            },
            risk_ids: {
              type: 'array',
              description: 'Optional array of risk IDs to apply this mitigation to',
              items: {
                type: 'integer',
              },
            },
          },
          required: ['description', 'status', 'priority', 'effort'],
        },
      },
    },
    async (args, context) => {
      if (!context.assessmentId) {
        throw new Error('No assessment selected');
      }

      const mitigationData: Omit<TmMitigation, 'id'> = {
        description: args.description,
        status: args.status,
        priority: args.priority,
        effort: args.effort,
        assessment_id: context.assessmentId,
      };

      const createdMitigation = await api.createTmMitigation(mitigationData);

      // Update Redux state if dispatch is available
      if (context.dispatch) {
        context.dispatch(addTmMitigation(createdMitigation));
      }

      // Link mitigation to risks if risk_ids provided
      const updatedRisks: TmRisk[] = [];
      if (args.risk_ids && args.risk_ids.length > 0 && createdMitigation.id) {
        for (const riskId of args.risk_ids) {
          try {
            // Get current risk to preserve existing mitigation_ids
            const currentRisk = await api.getTmRisk(riskId);
            const currentMitigationIds = currentRisk.mitigation_ids ||
              currentRisk.mitigations?.map(m => m.id).filter((id): id is number => id !== undefined) ||
              [];

            // Add new mitigation ID if not already present
            if (!currentMitigationIds.includes(createdMitigation.id)) {
              const updatedRisk = await api.updateTmRiskById(riskId, {
                mitigation_ids: [...currentMitigationIds, createdMitigation.id],
              });
              updatedRisks.push(updatedRisk);

              // Update Redux state for the risk
              if (context.dispatch) {
                context.dispatch(updateTmRisk(updatedRisk));
              }
            }
          } catch (error) {
            console.error(`Failed to link mitigation to risk ${riskId}:`, error);
          }
        }
      }

      return {
        success: true,
        data: createdMitigation,
        message: `Created mitigation: ${createdMitigation.description}${
          updatedRisks.length > 0 ? ` and linked to ${updatedRisks.length} risk(s)` : ''
        }`,
      };
    }
  );

  registry.register(
    'update_tm_mitigation',
    {
      type: 'function',
      function: {
        name: 'update_tm_mitigation',
        description: 'Update an existing mitigation. Use this to modify mitigation properties.',
        parameters: {
          type: 'object',
          properties: {
            mitigation_id: {
              type: 'integer',
              description: 'The ID of the mitigation to update',
            },
            description: {
              type: 'string',
              description: 'New description for the mitigation',
            },
            status: {
              type: 'string',
              description: 'New status',
            },
            priority: {
              type: 'integer',
              description: 'New priority (0-5)',
              minimum: 0,
              maximum: 5,
            },
            effort: {
              type: 'integer',
              description: 'New effort (0-5)',
              minimum: 0,
              maximum: 5,
            },
          },
          required: ['mitigation_id'],
        },
      },
    },
    async (args, context) => {
      const updateData: any = {};
      if (args.description !== undefined) updateData.description = args.description;
      if (args.status !== undefined) updateData.status = args.status;
      if (args.priority !== undefined) updateData.priority = args.priority;
      if (args.effort !== undefined) updateData.effort = args.effort;

      const updatedMitigation = await api.updateTmMitigationById(args.mitigation_id, updateData);

      // Update Redux state if dispatch is available
      if (context.dispatch) {
        context.dispatch(updateTmMitigation(updatedMitigation));
      }

      return {
        success: true,
        data: updatedMitigation,
        message: `Updated mitigation: ${updatedMitigation.description}`,
      };
    }
  );

  registry.register(
    'delete_tm_mitigation',
    {
      type: 'function',
      function: {
        name: 'delete_tm_mitigation',
        description: 'Delete a mitigation by ID. Use this when the user wants to remove a mitigation.',
        parameters: {
          type: 'object',
          properties: {
            mitigation_id: {
              type: 'integer',
              description: 'The ID of the mitigation to delete',
            },
          },
          required: ['mitigation_id'],
        },
      },
    },
    async (args, context) => {
      await api.deleteTmMitigationById(args.mitigation_id);

      // Update Redux state if dispatch is available
      if (context.dispatch) {
        context.dispatch(removeTmMitigation(args.mitigation_id));
      }

      return {
        success: true,
        message: `Deleted mitigation with ID ${args.mitigation_id}`,
      };
    }
  );

  registry.register(
    'link_mitigation_to_risk',
    {
      type: 'function',
      function: {
        name: 'link_mitigation_to_risk',
        description: 'Link an existing mitigation to one or more risks. Use this when applying an existing mitigation to risks.',
        parameters: {
          type: 'object',
          properties: {
            mitigation_id: {
              type: 'integer',
              description: 'The ID of the mitigation to link',
            },
            risk_ids: {
              type: 'array',
              description: 'Array of risk IDs to apply this mitigation to',
              items: {
                type: 'integer',
              },
            },
          },
          required: ['mitigation_id', 'risk_ids'],
        },
      },
    },
    async (args, context) => {
      const updatedRisks: TmRisk[] = [];
      const errors: string[] = [];

      for (const riskId of args.risk_ids) {
        try {
          // Get current risk to preserve existing mitigation_ids
          const currentRisk = await api.getTmRisk(riskId);
          const currentMitigationIds = currentRisk.mitigation_ids ||
            currentRisk.mitigations?.map(m => m.id).filter((id): id is number => id !== undefined) ||
            [];

          // Add mitigation ID if not already present
          if (!currentMitigationIds.includes(args.mitigation_id)) {
            const updatedRisk = await api.updateTmRiskById(riskId, {
              mitigation_ids: [...currentMitigationIds, args.mitigation_id],
            });
            updatedRisks.push(updatedRisk);

            // Update Redux state for the risk
            if (context.dispatch) {
              context.dispatch(updateTmRisk(updatedRisk));
            }
          }
        } catch (error: any) {
          const errorMsg = `Failed to link mitigation ${args.mitigation_id} to risk ${riskId}: ${error.message || error}`;
          console.error(errorMsg);
          errors.push(errorMsg);
        }
      }

      if (updatedRisks.length === 0 && errors.length > 0) {
        return {
          success: false,
          data: [],
          message: `Failed to link mitigation. Errors: ${errors.join('; ')}`,
        };
      }

      return {
        success: true,
        data: updatedRisks,
        message: `Successfully linked mitigation ${args.mitigation_id} to ${updatedRisks.length} risk(s)${errors.length > 0 ? `. Errors: ${errors.join('; ')}` : ''}. IMPORTANT: The linking is complete. Respond to the user confirming the link was created. DO NOT call list or link tools again.`,
      };
    }
  );

  registry.register(
    'unlink_mitigation_from_risk',
    {
      type: 'function',
      function: {
        name: 'unlink_mitigation_from_risk',
        description: 'Remove a mitigation from one or more risks. Use this when removing a mitigation from risks.',
        parameters: {
          type: 'object',
          properties: {
            mitigation_id: {
              type: 'integer',
              description: 'The ID of the mitigation to unlink',
            },
            risk_ids: {
              type: 'array',
              description: 'Array of risk IDs to remove this mitigation from',
              items: {
                type: 'integer',
              },
            },
          },
          required: ['mitigation_id', 'risk_ids'],
        },
      },
    },
    async (args, context) => {
      const updatedRisks: TmRisk[] = [];

      for (const riskId of args.risk_ids) {
        try {
          // Get current risk to get existing mitigation_ids
          const currentRisk = await api.getTmRisk(riskId);
          const currentMitigationIds = currentRisk.mitigation_ids ||
            currentRisk.mitigations?.map(m => m.id).filter((id): id is number => id !== undefined) ||
            [];

          // Remove mitigation ID if present
          if (currentMitigationIds.includes(args.mitigation_id)) {
            const updatedRisk = await api.updateTmRiskById(riskId, {
              mitigation_ids: currentMitigationIds.filter(id => id !== args.mitigation_id),
            });
            updatedRisks.push(updatedRisk);

            // Update Redux state for the risk
            if (context.dispatch) {
              context.dispatch(updateTmRisk(updatedRisk));
            }
          }
        } catch (error) {
          console.error(`Failed to unlink mitigation from risk ${riskId}:`, error);
        }
      }

      return {
        success: true,
        data: updatedRisks,
        message: `Unlinked mitigation from ${updatedRisks.length} risk(s)`,
      };
    }
  );

  // === TmAssessment Tools ===

  registry.register(
    'list_tm_assessments',
    {
      type: 'function',
      function: {
        name: 'list_tm_assessments',
        description: 'List all threat modeling assessments for the current organization. Use this to see available assessments.',
        parameters: {
          type: 'object',
          properties: {},
        },
      },
    },
    async (args, context) => {
      const assessments = await api.listTmAssessments();

      // Update Redux state if dispatch is available
      if (context.dispatch) {
        context.dispatch(setTmAssessments(assessments));
      }

      return {
        success: true,
        data: assessments,
        message: `Found ${assessments.length} assessment(s)`,
      };
    }
  );

  registry.register(
    'create_tm_assessment',
    {
      type: 'function',
      function: {
        name: 'create_tm_assessment',
        description: 'Create a new threat modeling assessment. Use this when the user wants to start a new assessment. If only a description is provided, a name will be auto-generated from it.',
        parameters: {
          type: 'object',
          properties: {
            name: {
              type: 'string',
              description: 'The name of the assessment (optional - will be auto-generated from description if not provided)',
            },
            description: {
              type: 'string',
              description: 'Description of the assessment',
            },
          },
          required: [],
        },
      },
    },
    async (args, context) => {
      // Auto-generate name from description if only description provided
      let name = args.name;
      const description = args.description;

      if (!name && description) {
        // Take first 50 chars of description as name
        name = description.length > 50 ? description.substring(0, 47) + '...' : description;
      } else if (!name && !description) {
        throw new Error('Either name or description must be provided');
      }

      const assessmentData: Omit<TmAssessment, 'id'> = {
        name: name!,
        description: description || undefined,
      };

      const createdAssessment = await api.createTmAssessment(assessmentData);

      // Update Redux state if dispatch is available
      if (context.dispatch) {
        context.dispatch(addTmAssessment(createdAssessment));
      }

      return {
        success: true,
        data: createdAssessment,
        message: `Created assessment: ${createdAssessment.name}`,
      };
    }
  );

  registry.register(
    'update_tm_assessment',
    {
      type: 'function',
      function: {
        name: 'update_tm_assessment',
        description: 'Update an existing threat modeling assessment. Use this to modify assessment properties.',
        parameters: {
          type: 'object',
          properties: {
            assessment_id: {
              type: 'integer',
              description: 'The ID of the assessment to update',
            },
            name: {
              type: 'string',
              description: 'New name for the assessment',
            },
            description: {
              type: 'string',
              description: 'New description',
            },
          },
          required: ['assessment_id'],
        },
      },
    },
    async (args, context) => {
      const updateData: any = {};
      if (args.name !== undefined) updateData.name = args.name;
      if (args.description !== undefined) updateData.description = args.description;

      const updatedAssessment = await api.updateTmAssessmentById(args.assessment_id, updateData);

      // Update Redux state if dispatch is available
      if (context.dispatch) {
        context.dispatch(updateTmAssessment(updatedAssessment));
      }

      return {
        success: true,
        data: updatedAssessment,
        message: `Updated assessment: ${updatedAssessment.name}`,
      };
    }
  );

  registry.register(
    'delete_tm_assessment',
    {
      type: 'function',
      function: {
        name: 'delete_tm_assessment',
        description: 'Delete a threat modeling assessment by ID. Use this when the user wants to remove an assessment.',
        parameters: {
          type: 'object',
          properties: {
            assessment_id: {
              type: 'integer',
              description: 'The ID of the assessment to delete',
            },
          },
          required: ['assessment_id'],
        },
      },
    },
    async (args, context) => {
      await api.deleteTmAssessmentById(args.assessment_id);

      // Update Redux state if dispatch is available
      if (context.dispatch) {
        context.dispatch(removeTmAssessment(args.assessment_id));
      }

      return {
        success: true,
        message: `Deleted assessment with ID ${args.assessment_id}`,
      };
    }
  );
}

/**
 * Get the system prompt for Threat Modeling module
 */
export function getTmSystemPrompt(
  assessment: { id?: number; name?: string; description?: string } | null,
  assessmentId?: number | null,
  risks?: TmRisk[],
  mitigations?: TmMitigation[],
  actors?: TmActor[]
): string {
  // Use assessment object if available, otherwise fall back to ID
  const effectiveId = assessment?.id || assessmentId;

  const assessmentInfo = effectiveId
    ? assessment && assessment.name
      ? `**${assessment.name}**${assessment.description ? `\n\n${assessment.description}` : ''}\n\n(Assessment ID: ${assessment.id})`
      : `**Assessment ID: ${effectiveId}** (selected)`
    : '**Not selected**';

  const contextNote = effectiveId
    ? '> You are working within the selected assessment. All operations will apply to this assessment.'
    : '> When no assessment is selected, inform the user they must select or create an assessment before proceeding with risk/mitigation/actor operations.';

  // Build current state summary
  let currentDataSummary = '';
  if (effectiveId && (risks || mitigations || actors)) {
    currentDataSummary = '\n\n**Current Data in UI:**\n\n';

    if (risks && risks.length > 0) {
      currentDataSummary += `**Risks (${risks.length}):**\n`;
      risks.forEach(r => {
        currentDataSummary += `- **${r.description}** (ID: ${r.id}) - Chance: ${r.chance}, Impact: ${r.impact}, Status: ${r.status}\n`;
      });
      currentDataSummary += '\n';
    } else if (risks) {
      currentDataSummary += '**Risks:** None\n\n';
    }

    if (mitigations && mitigations.length > 0) {
      currentDataSummary += `**Mitigations (${mitigations.length}):**\n`;
      mitigations.forEach(m => {
        currentDataSummary += `- **${m.description}** (ID: ${m.id}) - Priority: ${m.priority}, Status: ${m.status}\n`;
      });
      currentDataSummary += '\n';
    } else if (mitigations) {
      currentDataSummary += '**Mitigations:** None\n\n';
    }

    if (actors && actors.length > 0) {
      currentDataSummary += `**Actors (${actors.length}):**\n`;
      actors.forEach(a => {
        currentDataSummary += `- **${a.name}** (ID: ${a.id})\n`;
      });
      currentDataSummary += '\n';
    } else if (actors) {
      currentDataSummary += '**Actors:** None\n\n';
    }

    currentDataSummary += '> **IMPORTANT:** Use this data directly. DO NOT call list_tm_risks, list_tm_mitigations, or list_tm_actors unless the user explicitly asks to refresh the data. You already have the current state above.\n';
  }

  return `# Threat Modeling Module

## Context

You are assisting with threat modeling—a security practice that identifies potential threats to a system, assesses their risk, and defines mitigations. You have full CRUD access to the threat model database.
Users might be brief, in case the user seems to expect you to come up with details, try to fill in reasonable defaults based on best practices.

**Current Assessment:**

${assessmentInfo}

${contextNote}${currentDataSummary}

---

## Entity Model

| Entity | Description | Relationships |
|--------|-------------|---------------|
| **Assessment** | A threat modeling exercise scoped to a system or project | Contains risks, actors |
| **Risk** | A potential threat or vulnerability | Has many mitigations (via links), associated with actors |
| **Mitigation** | A control or countermeasure that reduces risk | Can be linked to multiple risks |
| **Actor** | A threat actor (attacker profile, persona) | Referenced by risks |

**Key relationship:** Risks and mitigations have a many-to-many relationship. One mitigation (e.g., "Enable MFA") can apply to multiple risks. One risk can have multiple mitigations.

---

## Available Operations

- **Assessments:** list, create, update, delete
- **Risks:** list, create, update, delete, bulk update
- **Mitigations:** list, create, update, delete, link to risks, unlink from risks
- **Actors:** list, create, update, delete

When creating mitigations, use the \`risk_ids\` parameter to automatically link them to risks.

---

## Action Classification

Interpret user intent based on their verb choice:

| User says | Intent | Your action |
|-----------|--------|-------------|
| "find", "search", "look for", "show", "what mitigations exist" | **SEARCH** | List existing items, present matches, wait for user decision |
| "create", "add", "come up with", "suggest", "generate" | **CREATE** | Create new items immediately using tools |
| "link", "connect", "apply", "associate" | **LINK** | Link existing items together |
| "update", "change", "modify", "set" | **UPDATE** | Modify existing items |
| "delete", "remove" | **DELETE** | Remove items (confirm if bulk) |

**Ambiguous case:** "Add a mitigation for risk 5"
- First check if suitable existing mitigations could be linked
- If yes: present options and ask "Should I link an existing mitigation or create a new one?"
- If no existing matches: create a new one directly

---

## Tool Usage Rules

### The One-Fetch Rule
Call each list/search tool **at most once** per user request (except for bulk requests). After receiving results, respond with text—do not re-fetch the same data.

### Reference Resolution
When the user says "that mitigation", "the risk", "that one":
1. Use IDs from your most recent tool results in this conversation
2. Do not call list tools again to find IDs you already have
3. If genuinely ambiguous, ask: "Which one do you mean: [option A] or [option B]?"

### Parallel Execution
You can make multiple tool calls in a single response. Use this for:
- Fetching risks AND mitigations simultaneously
- Creating multiple mitigations for different risks
- Bulk updates across multiple entities

### Bulk Operation Thresholds
| Operation | Count ≤ 5 | Count > 5 |
|-----------|-----------|-----------|
| CREATE | Execute immediately | Confirm first: "This will create X items. Proceed?" |
| DELETE | Confirm first | Confirm first with list preview |
| UPDATE | Execute immediately | Execute immediately |
| LINK/UNLINK | Execute immediately | Execute immediately |

---

## Response Format

**After listing/searching:**
Present results in a concise format:
\`\`\`
Found 3 mitigations that could apply to "SQL Injection Risk":
1. **Input Validation** (ID: 12) - Currently linked to 2 other risks
2. **Parameterized Queries** (ID: 15) - Not linked to any risks
3. **WAF Rules** (ID: 18) - Currently linked to 5 risks

Want me to link any of these, or create a new mitigation?
\`\`\`

**After creating/updating/deleting:**
Confirm succinctly:
\`\`\`
Created mitigation "Rate Limiting" (ID: 24) and linked it to risk "Brute Force Attack" (ID: 7).
\`\`\`

**Do not** follow up confirmations with additional tool calls or re-listing.

---

## Error Handling

| Scenario | Response |
|----------|----------|
| Entity not found | "I couldn't find a [risk/mitigation/actor] with ID X. Want me to list available ones?" |
| No assessment selected | "Please select or create an assessment first. Use 'list assessments' to see existing ones." |
| Ambiguous reference | "I found multiple matches for '[term]': [list]. Which one did you mean?" |
| Tool call fails | "The operation failed: [error]. Would you like me to retry or try a different approach?" |
| No matches found | "No existing mitigations match this risk. Want me to create one?" |

---

## Examples

### Search Operation
**User:** "Find mitigations for the authentication bypass risk"

**You:**
1. Call \`list_tm_mitigations\` (once)
2. Analyze results for relevant matches
3. Respond with text presenting options
4. Wait for user to decide on linking or creating

### Bulk Create Operation
**User:** "Create mitigations for all risks that don't have any"

**You:**
1. Call \`list_tm_risks\` to identify risks with empty \`mitigation_ids\`
2. For each unmitigated risk, call \`create_tm_mitigation\` with appropriate \`risk_ids\` (parallel)
3. Respond with summary of what was created

### Link Existing
**User:** "Link the MFA mitigation to risks 3, 5, and 8"

**You:**
1. Call \`link_tm_mitigation\` three times (parallel) for each risk
2. Respond: "Linked 'MFA' to risks 3, 5, and 8."

### Ambiguous Create
**User:** "Add a mitigation for the DDoS risk"

**You:**
1. Call \`list_tm_mitigations\` to check for existing relevant mitigations
2. If matches exist: "I found existing mitigations that might apply: [list]. Should I link one of these or create a new mitigation?"
3. If no matches: Create new mitigation directly


## Anti-Patterns to Avoid

- ❌ Calling the same list tool multiple times for one request
- ❌ Asking permission for small create/update operations
- ❌ Following a confirmation with more tool calls
- ❌ Creating one generic mitigation and applying it to all risks
- ❌ Re-fetching data to resolve "that one" when you have recent context
- ❌ Lengthy explanations after simple confirmations
`;
}
